import React, {useCallback, useMemo} from 'react';
import InternalTools from 'features/InternalTools/InternalTools';
import ComingSoonProductCard from 'features/products/components/ProductCard/ComingSoonProductCard';
import NewProductCard from 'features/products/components/ProductCard/ProductCard';
import {T_SHIRT_SUBCAT_IDS} from '../utils/constants/categoryConstants';
import {
  checkIsUnitPriceDisplayTestApplicable,
  UNITS_QTY
} from '../utils/unitPriceDisplayTest';
import EcoFriendlyBadge from './product_card/EcoFriendlyBadge';
import MadeInUSABadge from './product_card/MadeInUSABadge';
import OldProductCard from './product_card/ProductCard';
import {getProductCardInsightsData} from './utils/algoliaInsights';
import {hasNewBadge, hasRecommendationBadge} from './utils/algoliaRulesBadges';
import {buildProductImage} from './utils/imageSourceBuilder';
import {getInitialSelectedColorId} from './utils/product';
import {forceCoverImageForSubcategory} from './utils/productImage';
import productPath from './utils/productPath';

// Badge component (moved from CustomHits)
// TODO: Can be removed once we use the new ProductCard
const Badge = ({content, recommendation = false, secondary = false}) => {
  let className = 'pc-ProductCard-promoBadge';
  if (secondary) className += ' pc-ProductCard-promoBadge--secondary';
  if (recommendation) className += ' pc-ProductCard-promoBadge-recommendation';
  return <span className={className}>{content}</span>;
};

// Badge helper functions (moved from CustomHits)
// TODO: Can be removed once we use the new ProductCard
const lowerProductCardBadge = ({badges, hit}) => {
  // Early exit if badges are null
  if (!badges) return null;

  // Define badge types and associated components
  const badgeChecks = [
    {
      component: <EcoFriendlyBadge productName={hit.name} />,
      ids: badges.eco_style_badge_ids
    },
    {component: <MadeInUSABadge />, ids: badges.usa_style_badge_ids}
  ];

  // Find and return the first applicable badge component
  const applicableBadge = badgeChecks.find((badge) =>
    badge.ids?.includes(Number(hit.product_id || 0))
  );
  return applicableBadge ? applicableBadge.component : null;
};

// TODO: Can be removed once we use the new ProductCard
const upperProductCardBadge = ({badges, categoryId, hit, iframer}) => {
  if (!badges) return null;

  // Check for sale badge
  if (badges.sale_badge_ids?.includes(Number(hit.product_id || 0))) {
    return Badge({content: 'SALE', iframer, secondary: true});
  }

  // Check for recommendation badges only if not a site-wide search
  const badgeTypes = ['bestSeller', 'customerFavorite', 'staffPick'];
  const badgeLabels = {
    bestSeller: 'Best\nSeller',
    customerFavorite: 'Customer\nFave',
    staffPick: 'Staff\nPick'
  };
  const activeCategoryId = categoryId || hit.primary_category_id;
  const foundBadge = badgeTypes.find((badgeType) =>
    hasRecommendationBadge({
      activeCategoryId,
      badgeName: badgeType,
      badges,
      productId: hit.product_id
    })
  );
  if (foundBadge) {
    return Badge({
      content: badgeLabels[foundBadge],
      iframer,
      recommendation: true
    });
  }

  // Check for new badge
  if (hasNewBadge({badges, hit})) {
    return Badge({content: 'New!', iframer});
  }

  return null;
};

export default function ProductCardRenderer({
  backColor,
  badges,
  categoryId,
  categoryName,
  cid,
  compareHandler,
  compareStyles,
  currentFilters,
  currentSelectedColorFilters,
  defaultQuoteQty,
  defaultQuoteQtyMap,
  fireSelectProductEvent,
  firstImage,
  frontColor,
  hit,
  iframer,
  index,
  internalCatalog,
  isNewProductCardEnabled,
  minQtyToggleSet,
  page,
  productBadges,
  quantity,
  quickViewHandler,
  quoteQuantityDirty,
  showStyleId,
  showUnbundledPriceCategoryLevel,
  showUnbundledPriceSiteWideSearch,
  siteWideSearch,
  useDynamicPricing,
  ydhSettings
}) {
  // Helper function to check if color refinements are applied
  const hasColorRefinements = useMemo(() => {
    return (
      currentFilters.find(
        (refinementItem) => refinementItem.attribute === 'colors.simple_names'
      ) !== undefined
    );
  }, [currentFilters]);

  // Helper function to get filtered product colors - memoized for performance
  const getProductColors = useCallback(
    (product) => {
      let productColors =
        currentSelectedColorFilters.length > 0
          ? product.colors.filter((color) =>
              color.simple_names.some((colorName) =>
                currentSelectedColorFilters.includes(colorName)
              )
            )
          : product.colors;

      if (minQtyToggleSet) {
        productColors = productColors.filter((color) => color.min_qty === 1);
      }

      return productColors;
    },
    [currentSelectedColorFilters, minQtyToggleSet]
  );

  const productColors = isNewProductCardEnabled
    ? getProductColors(hit)
    : hit.colors;

  const filterColorId =
    currentSelectedColorFilters.length > 0 ? productColors[0]?.id : null;

  const productQuoteQty =
    defaultQuoteQty ||
    (siteWideSearch && defaultQuoteQtyMap[hit.primary_category_id]);

  const pricingQuantity = checkIsUnitPriceDisplayTestApplicable({
    defaultUnitPrices: hit.default_unit_prices,
    desiredQty: UNITS_QTY,
    styleType: hit.style_type
  })
    ? UNITS_QTY
    : productQuoteQty || quantity;

  const defaultQuoteQuantity = checkIsUnitPriceDisplayTestApplicable({
    defaultUnitPrices: hit.default_unit_prices,
    desiredQty: UNITS_QTY,
    styleType: hit.style_type
  })
    ? UNITS_QTY
    : productQuoteQty;

  const applicableQuantity =
    checkIsUnitPriceDisplayTestApplicable({
      defaultUnitPrices: hit.default_unit_prices,
      desiredQty: UNITS_QTY,
      styleType: hit.style_type
    }) && quantity
      ? UNITS_QTY
      : quantity || null;

  const isTshirtStyle = useMemo(() => {
    return (
      hit.categories?.some((category) =>
        T_SHIRT_SUBCAT_IDS.includes(category.id)
      ) || false
    );
  }, [hit]);

  const showUnbundledPrice = useMemo(() => {
    return (
      showUnbundledPriceCategoryLevel ||
      (showUnbundledPriceSiteWideSearch && isTshirtStyle)
    );
  }, [
    showUnbundledPriceCategoryLevel,
    showUnbundledPriceSiteWideSearch,
    isTshirtStyle
  ]);

  // Common props for all product cards
  const baseProps = {
    key: hit.product_id,
    minQtyToggleSet,
    showUnbundledPrice
  };

  // Common props for ComingSoonProductCard and NewProductCard
  const newCardCommonProps = {
    badges: (productBadges && productBadges[hit.product_id]) || null,
    displayAllSwatches: currentSelectedColorFilters.length !== 0,
    displayImageAsCover: forceCoverImageForSubcategory(hit.sub_category_names),
    getProductImageSrcData: (colorId, noMoreLaydowns = false) =>
      buildProductImage({
        categoryId,
        cid,
        hit,
        iframer,
        noMoreLaydowns,
        selectedColorId: colorId,
        siteWideSearch,
        ydhSettings
      }),
    initialSelectedColorId: getInitialSelectedColorId(
      hit,
      siteWideSearch ? hit.primary_category_id : categoryId
    ),
    name: hit.name,
    productColors,
    productPath: productPath(hit)
  };

  // Common props for NewProductCard and ProductCard
  const colorProps = {
    backColor,
    frontColor
  };

  // Render ComingSoonProductCard for preview products
  if (hit.status === 'preview') {
    return (
      <ComingSoonProductCard
        {...baseProps}
        {...newCardCommonProps}
        productId={Number(hit.product_id)}
        containerProps={getProductCardInsightsData(hit)}
        onSelectProduct={() => fireSelectProductEvent(hit, index + 1)}
      />
    );
  }

  return isNewProductCardEnabled ? (
    <NewProductCard
      {...baseProps}
      {...newCardCommonProps}
      {...colorProps}
      productId={Number(hit.product_id)}
      filterColorId={filterColorId}
      primaryCategoryId={hit.primary_category_id}
      categoryId={categoryId}
      status={hit.status}
      categoryImages={hit.category_images}
      ratingCount={Number(hit.rating_count)}
      ratingScore={Number(hit.rating_score)}
      generalComments={hit.general_comments}
      sizes={hit.sizes}
      defaultUnitPrices={hit.default_unit_prices}
      minQty={hit.minimum_quantity}
      noQtyLimit={Boolean(hit.no_minimum) || minQtyToggleSet}
      pricingQuantity={pricingQuantity}
      hasSinglesEnabledColors={hit.has_singles_enabled_colors}
      specialty={hit.specialty}
      rushDeliveryTurntimes={hit.rush_delivery_turntimes}
      decorationMethod={hit.decoration_method}
      containerProps={getProductCardInsightsData(hit)}
      onSelectProduct={() => fireSelectProductEvent(hit, index + 1)}
      hideDetails={Boolean(hit.metadata?.STYLES_TARGET_CATEGORY_PROMOTION)}
      {...(internalCatalog && {
        footerContent: (
          <InternalTools
            colorLimit={hit.color_limit}
            compareStyles={compareStyles}
            compareHandler={compareHandler}
            decorationMethod={hit.decoration_method}
            productName={hit.name}
            productPath={productPath(hit)}
            speciality={hit.specialty}
            styleId={hit.product_id}
          />
        )
      })}
    />
  ) : (
    <OldProductCard
      {...baseProps}
      {...colorProps}
      hit={hit}
      currentFilters={currentFilters}
      categoryId={categoryId}
      categoryName={categoryName}
      defaultQuoteQty={defaultQuoteQuantity}
      siteWideSearch={siteWideSearch}
      upperProductCardBadge={upperProductCardBadge({
        badges,
        categoryId,
        hit,
        iframer,
        siteWideSearch
      })}
      lowerProductCardBadge={lowerProductCardBadge({badges, hit})}
      colorFilterApplied={hasColorRefinements}
      internalCatalog={internalCatalog}
      quickViewHandler={quickViewHandler}
      showStyleId={showStyleId}
      quantity={applicableQuantity}
      useDynamicPricing={useDynamicPricing}
      iframer={iframer}
      cid={cid}
      ydhSettings={ydhSettings}
      firstImage={firstImage}
      isFirstHit={page === 1 && index === 0}
      compareHandler={compareHandler}
      compareStyles={compareStyles}
      quoteQuantityDirty={quoteQuantityDirty}
      styleType={hit.style_type}
      decorationMethod={hit.decoration_method}
    />
  );
}
